import { OPERATION_TYPE } from '../../operations/models/operations.models';

type TranslateFn = (key: string, params?: Record<string, unknown>) => string;

/**
 * Validación del JSON del modal editor (NIF-845).
 * Puro: sin HTTP ni DOM; recibe `instant` de i18n.
 */
export function validateWorkflowJsonPayload(data: unknown, instant: TranslateFn): string[] {
	const errors: string[] = [];
	const payload = data as Record<string, unknown> | null;

	if (!payload || typeof payload !== 'object') {
		errors.push(instant('WORKFLOWS.CREATE_JSON.INVALID_OBJECT', { field: 'root' }));
		return errors;
	}

	if (!payload['alias'] || typeof payload['alias'] !== 'string') {
		errors.push(instant('WORKFLOWS.CREATE_JSON.REQUIRED_FIELD', { field: 'alias' }));
	}

	if (!payload['api_key'] || typeof payload['api_key'] !== 'string') {
		errors.push(instant('WORKFLOWS.CREATE_JSON.REQUIRED_FIELD', { field: 'api_key' }));
	}

	if (!payload['design_config_uuid'] || typeof payload['design_config_uuid'] !== 'string') {
		errors.push(instant('WORKFLOWS.CREATE_JSON.REQUIRED_FIELD', { field: 'design_config_uuid' }));
	}

	if (!payload['config'] || typeof payload['config'] !== 'object') {
		errors.push(instant('WORKFLOWS.CREATE_JSON.INVALID_OBJECT', { field: 'config' }));
	} else {
		errors.push(...validateWorkflowJsonConfig(payload['config'], instant));
	}

	if (!Array.isArray(payload['operations'])) {
		errors.push(instant('WORKFLOWS.CREATE_JSON.INVALID_ARRAY', { field: 'operations' }));
	} else if ((payload['operations'] as unknown[]).length === 0) {
		errors.push(instant('WORKFLOWS.CREATE_JSON.NO_OPERATIONS'));
	} else {
		errors.push(...validateWorkflowJsonOperations(payload['operations'] as unknown[], instant));
	}

	return errors;
}

export function validateWorkflowJsonConfig(config: unknown, instant: TranslateFn): string[] {
	const errors: string[] = [];
	const cfg = config as Record<string, unknown>;

	const requiredFields = [
		'allowed_device',
		'version',
		'required_recording',
		'include_images',
		'delayed_process_warning',
		'sdk',
	];
	requiredFields.forEach((field) => {
		if (cfg[field] === undefined) {
			errors.push(instant('WORKFLOWS.CREATE_JSON.REQUIRED_FIELD', { field: `config.${field}` }));
		}
	});

	if (cfg['allowed_device'] && !Array.isArray(cfg['allowed_device'])) {
		errors.push(instant('WORKFLOWS.CREATE_JSON.INVALID_ARRAY', { field: 'config.allowed_device' }));
	}

	if (cfg['version'] && typeof cfg['version'] !== 'number') {
		errors.push(instant('WORKFLOWS.CREATE_JSON.INVALID_TYPE', { field: 'config.version', type: 'number' }));
	}

	const validation = cfg['validation'] as Record<string, unknown> | undefined;
	if (!validation || typeof validation !== 'object') {
		errors.push(instant('WORKFLOWS.CREATE_JSON.INVALID_OBJECT', { field: 'config.validation' }));
	} else {
		const vType = validation['type'];
		if (!vType) {
			errors.push(instant('WORKFLOWS.CREATE_JSON.REQUIRED_FIELD', { field: 'config.validation.type' }));
		} else if (typeof vType === 'string') {
			const allowed = ['auto', 'ok', 'ko', 'ok-ko'];
			if (!allowed.includes(vType)) {
				errors.push(
					instant('WORKFLOWS.CREATE_JSON.INVALID_TYPE', {
						field: 'config.validation.type',
						type: 'auto | ok | ko | ok-ko | object',
					}),
				);
			}
		} else if (typeof vType === 'object' && vType !== null) {
			const nested = vType as Record<string, unknown>;
			if (!nested['type']) {
				errors.push(
					instant('WORKFLOWS.CREATE_JSON.REQUIRED_FIELD', { field: 'config.validation.type.type' }),
				);
			}
			if ('errors' in nested && !Array.isArray(nested['errors'])) {
				errors.push(
					instant('WORKFLOWS.CREATE_JSON.INVALID_ARRAY', {
						field: 'config.validation.type.errors',
					}),
				);
			}
		} else {
			errors.push(
				instant('WORKFLOWS.CREATE_JSON.INVALID_TYPE', {
					field: 'config.validation.type',
					type: 'string | object',
				}),
			);
		}
	}

	return errors;
}

export function validateWorkflowJsonOperations(operations: unknown[], instant: TranslateFn): string[] {
	const errors: string[] = [];
	const validTypes = [
		OPERATION_TYPE.HOME,
		OPERATION_TYPE.FORM,
		OPERATION_TYPE.OCR,
		OPERATION_TYPE.POL,
		OPERATION_TYPE.DOCUMENTS,
		OPERATION_TYPE.CONNECTIONS,
		OPERATION_TYPE.FINISH,
	];

	operations.forEach((operation: unknown, index) => {
		const op = operation as Record<string, unknown>;
		if (!op['type'] || !validTypes.includes(op['type'] as OPERATION_TYPE)) {
			errors.push(
				instant('WORKFLOWS.CREATE_JSON.INVALID_OPERATION_TYPE', {
					types: `"${validTypes.join('", "')}"`,
				}),
			);
		}

		const opConfig = op['config'];
		if (!opConfig || typeof opConfig !== 'object') {
			errors.push(
				instant('WORKFLOWS.CREATE_JSON.INVALID_OBJECT', {
					field: `operations[${index}].config`,
				}),
			);
			return;
		}

		if (
			op['type'] === OPERATION_TYPE.FORM &&
			!Array.isArray((opConfig as Record<string, unknown>)['steps'])
		) {
			errors.push(
				instant('WORKFLOWS.CREATE_JSON.INVALID_ARRAY', {
					field: `operations[${index}].config.steps`,
				}),
			);
		}
	});

	return errors;
}
